import api from '../../utils/api';
import { DoctorClinicBranch, DoctorClinicBranchTimeslot } from '../../models/MasterAdmin/doctorClinic';

export interface SpecialityOption {
  specialityId: string;
  speciality: string;
}

export interface VendorSpecialitiesResponse {
  specialities?: string[];
  specialityOptions?: SpecialityOption[];
}

export interface CreateTimeslotPayload {
  vendorId: string;
  consultationType: 'Online' | 'Offline';
  clinicBranchId?: string | null;
  day: string;
  specialityId: string;
  startTime: string;
  endTime: string;
  intervalMinutes: number;
  isActive?: boolean;
}

export interface BulkCreateTimeslotPayload {
  vendorId: string;
  consultationType: 'Online' | 'Offline';
  clinicBranchId?: string | null;
  day: string;
  templates: Array<{
    specialityId: string;
    startTime: string;
    endTime: string;
    intervalMinutes: number;
    isActive?: boolean;
  }>;
}

export interface CreateClinicBranchPayload {
  clinicName: string;
  address: string;
  pincode: string;
  latitude?: number | null;
  longitude?: number | null;
}

export const createClinicBranch = async (vendorId: string, payload: CreateClinicBranchPayload) => {
  const response = await api.post(`/clinic/branches/vendor/${vendorId}`, payload);
  return response.data as DoctorClinicBranch;
};

export const deleteClinicBranch = async (clinicBranchId: string) => {
  const response = await api.delete(`/clinic/branches/${clinicBranchId}`);
  return response.data;
};

export const deleteBranchTimeslots = async (vendorId: string, clinicBranchId: string) => {
  const response = await api.delete(`/clinic-timeslots/vendor/${vendorId}/branch/${clinicBranchId}`);
  return response.data as { deletedCount?: number; message?: string; deleted?: unknown[] };
};

export const fetchClinicBranches = async (
  vendorId: string,
  includeInactive = true
): Promise<DoctorClinicBranch[]> => {
  const response = await api.get(`/clinic/branches/vendor/${vendorId}`, {
    params: { includeInactive },
  });
  return Array.isArray(response.data) ? response.data : [];
};

export const fetchVendorTimeslots = async (
  vendorId: string,
  consultationType?: 'Online' | 'Offline' | null
): Promise<DoctorClinicBranchTimeslot[]> => {
  const params: Record<string, string> = { includeGeneratedSlots: 'true' };
  if (consultationType) params.consultationType = consultationType;
  const response = await api.get(`/clinic-timeslots/vendor/${vendorId}`, { params });
  if (Array.isArray(response.data)) return response.data;
  return Array.isArray(response.data?.timeslots) ? response.data.timeslots : [];
};

export const fetchVendorSpecialities = async (
  vendorId: string
): Promise<VendorSpecialitiesResponse> => {
  const response = await api.get(`/clinic-timeslots/vendor/${vendorId}/specialities`);
  return response.data || { specialities: [], specialityOptions: [] };
};

export const createTimeslot = async (payload: CreateTimeslotPayload) => {
  const response = await api.post('/clinic-timeslots', payload);
  return response.data;
};

export const bulkCreateTimeslots = async (payload: BulkCreateTimeslotPayload) => {
  const response = await api.post('/clinic-timeslots/bulk', payload);
  return response.data;
};

export const updateTimeslot = async (
  timeSlotID: string,
  payload: Partial<CreateTimeslotPayload> & { speciality?: string }
) => {
  const response = await api.put(`/clinic-timeslots/${timeSlotID}`, payload);
  return response.data;
};

export const toggleTimeslotStatus = async (timeSlotID: string) => {
  const response = await api.patch(`/clinic-timeslots/${timeSlotID}/toggle-status`);
  return response.data;
};

export const deleteTimeslot = async (timeSlotID: string) => {
  const response = await api.delete(`/clinic-timeslots/${timeSlotID}`);
  return response.data;
};

export const deleteTimeslotsForBranchDay = async (
  vendorId: string,
  clinicBranchId: string,
  day: string
) => {
  const encodedDay = encodeURIComponent(day);
  const response = await api.delete(
    `/clinic-timeslots/vendor/${vendorId}/branch/${clinicBranchId}/day/${encodedDay}`
  );
  return response.data;
};
